/**
 * Offline audio storage with encryption using IndexedDB + Web Crypto API.
 * Stores encrypted audio blobs for offline playback, with per-user keys.
 */

const DB_NAME = 'audioguide_offline';
const DB_VERSION = 1;
const STORE_NAME = 'encrypted_audio';
const KEY_STORE = 'encryption_keys';

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(KEY_STORE)) {
        db.createObjectStore(KEY_STORE, { keyPath: 'id' });
      }
    };
  });
}

async function getOrCreateKey(attractionId) {
  const db = await openDB();

  // Try to get existing key
  const existing = await new Promise((resolve, reject) => {
    const tx = db.transaction(KEY_STORE, 'readonly');
    const store = tx.objectStore(KEY_STORE);
    const req = store.get(attractionId);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

  if (existing) {
    return await crypto.subtle.importKey(
      'jwk', existing.jwk, { name: 'AES-GCM' }, true, ['encrypt', 'decrypt']
    );
  }

  // Generate new key
  const key = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']
  );
  const jwk = await crypto.subtle.exportKey('jwk', key);

  await new Promise((resolve, reject) => {
    const tx = db.transaction(KEY_STORE, 'readwrite');
    const store = tx.objectStore(KEY_STORE);
    store.put({ id: attractionId, jwk });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });

  return key;
}

async function encryptData(data, key) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv }, key, data
  );
  return { iv: Array.from(iv), data: new Uint8Array(encrypted) };
}

async function decryptData(encryptedObj, key) {
  const iv = new Uint8Array(encryptedObj.iv);
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv }, key, encryptedObj.data
  );
  return decrypted;
}

/**
 * Save an encrypted audio track to IndexedDB
 */
export async function saveTrackOffline(attractionId, track, audioArrayBuffer) {
  const key = await getOrCreateKey(attractionId);
  const encrypted = await encryptData(audioArrayBuffer, key);

  const db = await openDB();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.put({
      id: `${attractionId}_${track.id}`,
      attractionId,
      trackId: track.id,
      title: track.title,
      language: track.language,
      transcript: track.transcript,
      image_url: track.image_url,
      iv: encrypted.iv,
      data: encrypted.data,
      savedAt: new Date().toISOString()
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Load and decrypt an offline audio track, return as Blob URL
 */
export async function loadTrackOffline(attractionId, trackId) {
  const db = await openDB();
  const record = await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.get(`${attractionId}_${trackId}`);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

  if (!record) return null;

  const key = await getOrCreateKey(attractionId);
  const decrypted = await decryptData(record, key);
  const blob = new Blob([decrypted], { type: 'audio/mpeg' });
  return URL.createObjectURL(blob);
}

/**
 * Get list of offline tracks for an attraction
 */
export async function getOfflineTracks(attractionId) {
  const db = await openDB();
  const all = await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

  return all
    .filter(r => r.attractionId === attractionId)
    .reduce((acc, r) => {
      acc[r.trackId] = { title: r.title, savedAt: r.savedAt };
      return acc;
    }, {});
}

/**
 * Delete all offline tracks for an attraction
 */
export async function deleteOfflineTracks(attractionId) {
  const db = await openDB();
  const all = await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

  const toDelete = all.filter(r => r.attractionId === attractionId);
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    toDelete.forEach(r => store.delete(r.id));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Get total offline storage usage in bytes
 */
export async function getOfflineStorageSize() {
  const db = await openDB();
  const all = await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return all.reduce((sum, r) => sum + (r.data?.byteLength || 0), 0);
}
